// GuardiansPanel — list, add, and edit guardians (parents)
// Phone + password are required for the guardian app login — phone doubles
// as the login identifier, so there's no separate login ID to set.
import { useEffect, useState } from 'react';
import { getGuardians, createGuardian, updateGuardian } from '../api/client';
import MapPickerModal from './MapPickerModal';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import { Card, CardHeader } from '../ui/Card';
import { GeneratedPasswordBanner } from './GuardianPicker';

const EMPTY = { name: '', phone: '', email: '', password: '', latitude: '', longitude: '' };

export default function GuardiansPanel() {
  const [guardians, setGuardians] = useState([]);
  const [form,      setForm]      = useState(EMPTY);
  const [editing,   setEditing]   = useState(null);
  const [msg,       setMsg]       = useState('');
  const [justCreated, setJustCreated] = useState(null);
  const [showMapPicker, setShowMapPicker] = useState(false);

  useEffect(() => { load(); }, []);

  async function load() {
    try { setGuardians(await getGuardians()); } catch { setMsg('Failed to load'); }
  }

  function startEdit(g) {
    setEditing(g.id);
    setForm({
      name: g.name, phone: g.phone, email: g.email || '', password: '',
      latitude: g.latitude ?? '', longitude: g.longitude ?? '',
    });
  }

  function cancel() { setEditing(null); setForm(EMPTY); setMsg(''); }

  function set(key) { return e => setForm({ ...form, [key]: e.target.value }); }

  async function save(e) {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        // Trimmed here, not just at login — a stray trailing space baked into
        // the stored password hash at creation time can never be fixed by
        // trimming later at login, since the hash itself would be wrong.
        phone:     form.phone.trim(),
        password:  form.password.trim(),
        latitude:  form.latitude  === '' ? null : parseFloat(form.latitude),
        longitude: form.longitude === '' ? null : parseFloat(form.longitude),
      };
      const saved = editing ? await updateGuardian(editing, payload) : await createGuardian(payload);
      setMsg(editing ? 'Guardian updated ✅' : 'Guardian added ✅');
      if (!editing && saved.generated_password) setJustCreated(saved);
      cancel(); load();
    } catch (err) {
      setMsg('Error: ' + (err.response?.data?.error || err.message));
    }
  }

  const inputCls = "border border-line rounded-s px-3 py-2 text-sm bg-paper-raised focus:outline-none focus:ring-2 focus:ring-transit mt-1";
  const labelCls = "flex flex-col text-xs font-bold text-ink-soft";

  const missingLocationCount = guardians.filter(g => g.latitude == null || g.longitude == null).length;

  return (
    <div>
      <h2 className="font-display font-extrabold text-xl text-ink mb-1">Guardians (Parents)</h2>
      <p className="text-sm text-ink-soft mb-4">
        Each guardian uses their <strong className="text-ink">Phone Number</strong> + <strong className="text-ink">Password</strong> to log in to the parent app.
      </p>

      <Card className="p-5 mb-6 max-w-[480px] flex flex-col gap-3">
        <h3 className="font-display font-bold text-[15px] text-ink m-0">{editing ? 'Edit Guardian' : 'Add Guardian'}</h3>
        {justCreated && <GeneratedPasswordBanner guardian={justCreated} onDismiss={() => setJustCreated(null)} />}
        <form onSubmit={save} className="flex flex-col gap-3">
          <label className={labelCls}>Full Name
            <input className={inputCls} required value={form.name} onChange={set('name')} />
          </label>
          <label className={labelCls}>Phone
            <input className={inputCls} required value={form.phone} onChange={set('phone')} />
          </label>
          <label className={labelCls}>Email
            <input className={inputCls} type="email" value={form.email} onChange={set('email')} />
          </label>
          <label className={labelCls}>
            {editing ? 'New Password (leave blank to keep current)' : 'Password (leave blank to auto-generate)'}
            <input className={inputCls} type="password" value={form.password} onChange={set('password')}
              placeholder={editing ? '' : 'Auto-generated if left blank'} />
          </label>
          <label className={labelCls}>Home / Pickup Location
            <Button type="button" variant="secondary" className="mt-1" onClick={() => setShowMapPicker(true)}>
              {form.latitude
                ? `📍 ${parseFloat(form.latitude).toFixed(4)}, ${parseFloat(form.longitude).toFixed(4)}`
                : '📍 Search & Pin on Map'}
            </Button>
          </label>
          <div className="flex gap-2">
            <Button type="submit">{editing ? 'Update' : 'Add Guardian'}</Button>
            {editing && <Button variant="secondary" type="button" onClick={cancel}>Cancel</Button>}
          </div>
          {msg && (
            <p className={`text-sm font-bold px-3 py-2 rounded-s inline-block w-fit ${msg.includes('Error') ? 'bg-critical-tint text-critical' : 'bg-success-tint text-success'}`}>
              {msg}
            </p>
          )}
        </form>
      </Card>

      {showMapPicker && (
        <MapPickerModal
          initialLat={form.latitude  ? parseFloat(form.latitude)  : undefined}
          initialLng={form.longitude ? parseFloat(form.longitude) : undefined}
          onConfirm={(lat, lng) => {
            setForm({ ...form, latitude: String(lat), longitude: String(lng) });
            setShowMapPicker(false);
          }}
          onClose={() => setShowMapPicker(false)}
        />
      )}

      {guardians.length > 0 && (
        <p className="text-sm mb-3">
          {missingLocationCount > 0
            ? <span className="text-caution font-bold">⚠️ {missingLocationCount} of {guardians.length} guardians have no location set</span>
            : <span className="text-success font-bold">✅ All guardians have a location set</span>}
        </p>
      )}

      <Card>
        <CardHeader title={`Guardians (${guardians.length})`} />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-ink-soft bg-paper">
                <th className="px-4 py-2.5">ID</th><th className="px-4 py-2.5">Name</th>
                <th className="px-4 py-2.5">Phone</th><th className="px-4 py-2.5">Email</th>
                <th className="px-4 py-2.5">Location</th>
                <th className="px-4 py-2.5">Onboarding</th><th className="px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {guardians.map(g => (
                <tr key={g.id} className="border-t border-line-soft hover:bg-transit-tint/40">
                  <td className="px-4 py-3 text-ink-soft tabular-nums">{g.id}</td>
                  <td className="px-4 py-3 font-semibold text-ink">{g.name}</td>
                  <td className="px-4 py-3">{g.phone}</td>
                  <td className="px-4 py-3">{g.email || '—'}</td>
                  <td className="px-4 py-3">
                    {g.latitude != null
                      ? <span className="text-[11px] text-success">📍 {Number(g.latitude).toFixed(4)}, {Number(g.longitude).toFixed(4)}</span>
                      : <span className="text-[11px] text-caution">Not set</span>}
                  </td>
                  <td className="px-4 py-3">
                    {g.location_set
                      ? <div>
                          <Badge tone="success">Location Set</Badge>
                          {g.pickup_address && <div className="text-[11px] text-ink-soft mt-1">{g.pickup_address}</div>}
                        </div>
                      : <Badge tone="caution">Pending Setup</Badge>}
                  </td>
                  <td className="px-4 py-3">
                    <Button variant="secondary" className="!px-2.5 !py-1 !text-xs" onClick={() => startEdit(g)}>Edit</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
